import { Select, Typography } from 'antd';

/**
 * "…and match it against": the half of a scan that makes it find anything.
 *
 * A scan into a lookup has to decide WHAT the decoded string is. Left alone
 * it is the record's own id, which is right for a symbol this platform
 * printed and wrong for every barcode that was already on the thing before
 * keepr existed — an asset tag, a VIN plate, a serial sticker. That is the
 * case `options.scan.match` was built for on the API side, and until this
 * control there was no way to reach it from the product at all: the option
 * existed, was documented, was validated, and no screen wrote it.
 *
 * It names an element on the TARGET card, not on this one — the scan looks
 * the OTHER record up — which is why the choices come from the lookup card
 * rather than from the siblings beside this element.
 */

const { Text } = Typography;

const labelOf = el => el?.label?.singular || el?.name || '';

export default function ScanOptions({ element, index, targetElements = [], targetLabel = 'the looked-up record', onChange, basePath = null }) {
	const scan = element?.options?.scan;
	// Nothing to configure until the camera button is on.
	if (!scan) return null;

	const path = `${basePath || `elements[${ index }]`}.options.scan`;
	const match = (scan && typeof scan === 'object' && scan.match) || undefined;

	// Only fields a printed string can be compared against. A scan hands back
	// text; matching it against a date or a number would be a query that can
	// only ever miss.
	const choices = (targetElements || [])
		.filter(el => el?.name && [ 'text-small', 'text-large', 'url', 'email', 'phone' ].includes(el.dataType))
		.map(el => ({ value: el.name, label: labelOf(el) }));

	if (!choices.length) {
		return (
			<Text type='secondary' className='d-block'>
				A scan here is read as the record’s own id — the code this platform prints.
				{' '}Give {targetLabel} a text field (an asset tag, a serial number) to match a code that was already on the thing.
			</Text>
		);
	}

	return (
		<div className='card-def-element__scan-match'>
			<Select
				style={{ minWidth: 220 }}
				allowClear
				value={match}
				placeholder='The record’s own id'
				aria-label='Match the scanned code against'
				options={choices}
				// `true` is the option's "just a camera button" shape; an
				// object is only stored when there is something in it.
				onChange={value => onChange(path, value ? { ...(typeof scan === 'object' ? scan : {}), match: value } : true)}
			/>
			<Text type='secondary' className='d-block'>
				What the scanned code is. Left alone it is the record’s own id, which is what a keepr label carries;
				{' '}point it at a field of {targetLabel} to read a barcode that was already on the thing.
			</Text>
		</div>
	);
}
